import _axios from '../api';

const ADVERTS_API = '/app-adverts';
const APPS_API = '/apps';

// The shared interceptor rejects with `error.response || error` (utils/index.js, pinned by
// apiErrorRejection.test.js). So whenever the server actually answered - a 404 before the
// store-manager rollout, a 500, a 409 - the rejection IS the response and has no `.message`;
// the body is on `.data`. Only a request that never reached the server carries `.message`.
// Reading `err.message` alone therefore produced `undefined` for every error a user can see.
const messageOf = (err, fallback) => err?.data?.message || err?.message || fallback;

// The two list tabs re-query on every search keystroke, sort and page change, so a slow answer to an older query
// could land after a newer one and put the wrong page on screen. Each list counts its requests and applies only
// the latest one's answer; a superseded request resolves with null.
const latestOf = () => {
  let current = 0;
  return () => {
    const mine = ++current;
    return () => mine === current;
  };
};
const advertsRequest = latestOf();
const appsPageRequest = latestOf();
const appCountsRequest = latestOf();

const pageParams = ({ search, page, size, sortBy, direction, filter }) => {
  const params = { page, size };
  if (search) params.search = search;
  if (filter) params.filter = filter;
  if (sortBy) params['sort-by'] = sortBy;
  if (direction) params.direction = direction;
  return params;
};

/**
 * One page of the Manage App Adverts tab (UN-3339). Server-paged and server-searched: the tab holds a page and
 * its total, not every advert. `search` matches the advert's name, description, placement and type and the
 * linked app's name — the fields the tab used to match client-side.
 */
export function getAdverts({ search = '', page = 0, size = 20, sortBy = null, direction = null } = {}) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const isLatest = advertsRequest();
      _axios
        .get(ADVERTS_API, { params: pageParams({ search, page, size, sortBy, direction }) })
        .then(response => {
          if (!isLatest()) return resolve(null);
          dispatch({
            type: 'APPSTORE_ADVERTS_PAGE_SUCCESS',
            payload: response.data
          });
          resolve(response.data);
        })
        .catch(err => {
          if (isLatest()) {
            dispatch({
              type: 'APPSTORE_ADVERTS_PAGE_ERROR',
              payload: messageOf(err, 'Could not load app adverts.')
            });
          }
          reject(err);
        });
    });
}

/**
 * The whole catalog, unpaged — what the advert form's app picker needs to offer every app. The All Apps tab reads
 * it a page at a time through getAppsPage instead.
 */
export function getApps(sortBy = null, direction = null) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const params = {};
      if (sortBy) params['sort-by'] = sortBy;
      if (direction) params.direction = direction;
      _axios
        .get(APPS_API, { params })
        .then(response => {
          dispatch({
            type: 'APPSTORE_APPS_GETALL_SUCCESS',
            payload: response.data
          });
          resolve(response.data);
        })
        .catch(err => {
          dispatch({
            type: 'APPSTORE_APPS_GETALL_ERROR',
            payload: messageOf(err, 'Could not load the app catalog.')
          });
          reject(err);
        });
    });
}

/**
 * One page of the All Apps tab (UN-3339): `filter` is all | with-advert | no-advert, and `search` matches the
 * app's name, author, type, category, source and source key.
 */
export function getAppsPage({
  filter = 'all',
  search = '',
  page = 0,
  size = 20,
  sortBy = null,
  direction = null
} = {}) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const isLatest = appsPageRequest();
      _axios
        .get(APPS_API, { params: pageParams({ filter, search, page, size, sortBy, direction }) })
        .then(response => {
          if (!isLatest()) return resolve(null);
          dispatch({
            type: 'APPSTORE_APPS_PAGE_SUCCESS',
            payload: response.data
          });
          resolve(response.data);
        })
        .catch(err => {
          if (isLatest()) {
            dispatch({
              type: 'APPSTORE_APPS_PAGE_ERROR',
              payload: messageOf(err, 'Could not load the app catalog.')
            });
          }
          reject(err);
        });
    });
}

/**
 * The All / With advert / No advert chip counts over one search. The tab holds one page, so it can no longer count
 * the subsets itself. A failure leaves the chips without numbers rather than failing the tab.
 */
export function getAppCounts(search = '') {
  return dispatch =>
    new Promise((resolve, reject) => {
      const isLatest = appCountsRequest();
      _axios
        .get(`${APPS_API}/counts`, { params: search ? { search } : {} })
        .then(response => {
          if (!isLatest()) return resolve(null);
          dispatch({ type: 'APPSTORE_APP_COUNTS_SUCCESS', payload: response.data });
          resolve(response.data);
        })
        .catch(err => {
          if (isLatest()) dispatch({ type: 'APPSTORE_APP_COUNTS_ERROR' });
          reject(err);
        });
    });
}

export function deleteAdvert(uuid) {
  return dispatch =>
    new Promise((resolve, reject) => {
      _axios
        .delete(`${ADVERTS_API}/${uuid}`)
        .then(() => {
          dispatch({
            type: 'APPSTORE_ADVERT_DELETE_SUCCESS',
            payload: uuid
          });
          resolve(uuid);
        })
        .catch(err => {
          dispatch({
            type: 'APPSTORE_ADVERT_DELETE_ERROR',
            payload: messageOf(err, 'Could not delete the advert.')
          });
          reject(err);
        });
    });
}

/**
 * The advert detail (UN-3248). Every mutation below answers with the SAME full detail payload, so each
 * one refreshes the form through APPSTORE_ADVERT_GET_SUCCESS rather than a bespoke merge — the server
 * derives visibility, locks Not Visible over the other flags and stamps the audit fields, and a
 * client-side merge would show a state the server does not hold.
 */
export function getAdvert(uuid) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({ type: 'APPSTORE_ADVERT_GET_REQUEST' });
      _axios
        .get(`${ADVERTS_API}/${uuid}`)
        .then(response => {
          dispatch({ type: 'APPSTORE_ADVERT_GET_SUCCESS', payload: response.data });
          resolve(response.data);
        })
        .catch(err => {
          dispatch({
            type: 'APPSTORE_ADVERT_GET_ERROR',
            payload: messageOf(err, 'Could not load the advert.')
          });
          reject(err);
        });
    });
}

// `send` is a factory, not a promise: taking the promise itself would fire the request when the
// action creator was CALLED rather than when the thunk is dispatched — so a mutation built but never
// dispatched would still have hit the server.
const mutate = (send, fallback) => dispatch =>
  new Promise((resolve, reject) => {
    send()
      .then(response => {
        dispatch({ type: 'APPSTORE_ADVERT_GET_SUCCESS', payload: response.data });
        resolve(response.data);
      })
      .catch(err => {
        dispatch({ type: 'APPSTORE_ADVERT_SAVE_ERROR', payload: messageOf(err, fallback) });
        reject(err);
      });
  });

export function createAdvert(body) {
  return mutate(() => _axios.post(ADVERTS_API, body), 'Could not create the advert.');
}

export function updateAdvert(uuid, body) {
  return mutate(() => _axios.put(`${ADVERTS_API}/${uuid}`, body), 'Could not save the advert.');
}

export function publishAdvert(uuid) {
  return mutate(() => _axios.put(`${ADVERTS_API}/${uuid}/publish`), 'Could not publish the advert.');
}

export function unpublishAdvert(uuid) {
  return mutate(() => _axios.put(`${ADVERTS_API}/${uuid}/unpublish`), 'Could not unpublish the advert.');
}

export function linkAdvert(uuid, appUuid) {
  return mutate(() => _axios.put(`${ADVERTS_API}/${uuid}/link`, { appUuid }), 'Could not link the app.');
}

export function unlinkAdvert(uuid) {
  return mutate(() => _axios.put(`${ADVERTS_API}/${uuid}/unlink`), 'Could not unlink the app.');
}

/** Uploads the advert image and resolves with its public URL; the form persists it as imageUrl. */
export function uploadAdvertImage(file) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const body = new FormData();
      body.append('file', file);
      // Content-Type is deliberately cleared rather than set to multipart/form-data: the shared axios
      // instance defaults every request to application/json, and a multipart header written by hand
      // carries no boundary, which Spring rejects. Undefined removes the header so the browser writes
      // its own with the boundary.
      _axios
        .post(`${ADVERTS_API}/images`, body, { headers: { 'Content-Type': undefined } })
        .then(response => resolve(response.data))
        .catch(err => {
          dispatch({
            type: 'APPSTORE_ADVERT_SAVE_ERROR',
            payload: messageOf(err, 'Could not upload the image.')
          });
          reject(err);
        });
    });
}

/** A gallery asset (screenshot or preview video, UN-3334): same multipart shape as the icon, answers {kind, url, contentType}. */
export function uploadAdvertAsset(file) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const body = new FormData();
      body.append('file', file);
      _axios
        .post(`${ADVERTS_API}/assets`, body, { headers: { 'Content-Type': undefined } })
        .then(response => resolve(response.data))
        .catch(err => {
          dispatch({
            type: 'APPSTORE_ADVERT_SAVE_ERROR',
            payload: messageOf(err, 'Could not upload the asset.')
          });
          reject(err);
        });
    });
}

export function getApp(uuid) {
  return dispatch =>
    new Promise((resolve, reject) => {
      _axios
        .get(`${APPS_API}/${uuid}`)
        .then(response => {
          dispatch({ type: 'APPSTORE_APP_GET_SUCCESS', payload: response.data });
          resolve(response.data);
        })
        .catch(err => {
          dispatch({
            type: 'APPSTORE_APP_GET_ERROR',
            payload: messageOf(err, 'Could not load the app.')
          });
          reject(err);
        });
    });
}

/** Installed sites for an app — server-paged (Spring Page) and server-searched, unlike the list tabs. */
export function getInstalledSites(appUuid, options) {
  return fetchInstalledSites(`${APPS_API}/${appUuid}/installed-sites`, options);
}

/**
 * Installed sites for ONE advert: only the stores this advert installed the app on — the set the Manage App Adverts
 * count counts. The app's own list also holds direct installs, other adverts' and backfills (UN-3252 QA).
 */
export function getAdvertInstalledSites(advertUuid, options) {
  return fetchInstalledSites(`${ADVERTS_API}/${advertUuid}/installed-sites`, options);
}

// Tags every installed-sites fetch, so the reducer can drop an answer a newer fetch superseded: a slow
// search landing after a fast one, a page landing after the next page, or one modal's page landing after
// another modal opened — all share the one `sites` slot.
let sitesRequestSeq = 0;

function fetchInstalledSites(url, { search = '', page = 0, size = 20 } = {}) {
  return dispatch =>
    new Promise((resolve, reject) => {
      const params = { page, size };
      if (search) params.search = search;
      sitesRequestSeq += 1;
      const requestId = sitesRequestSeq;
      dispatch({ type: 'APPSTORE_SITES_GET_REQUEST', requestId });
      _axios
        .get(url, { params })
        .then(response => {
          dispatch({ type: 'APPSTORE_SITES_GET_SUCCESS', payload: response.data, requestId });
          resolve(response.data);
        })
        .catch(err => {
          dispatch({
            type: 'APPSTORE_SITES_GET_ERROR',
            payload: messageOf(err, 'Could not load the installed sites.'),
            requestId
          });
          reject(err);
        });
    });
}

/** New App Advert: drop the previously opened advert and any error it left in the store. */
export function clearAdvert() {
  return { type: 'APPSTORE_ADVERT_CLEAR' };
}


